<ng-container *uiModalDescription>
  <div class="form-title" *ngIf="!loading && isActionAllowed('update')">
    <div class="metric-name-container">
      <i class="sp-mr-6" ui-icon uiType="metric" uiTheme="fill"></i>
      <span>{{ metric.name }}</span>
    </div>

    <ui-assignee-avatar-group [uiMax]="2" uiSize="sm">
      <ui-assignee-avatar *ngFor="let assigneeId of ownerIds" [fromAssigneeId]="assigneeId" />
    </ui-assignee-avatar-group>

    <div class="info" *ngIf="customFieldsLength > 0">{{ "all_required_fields_are_marked_with_an_asterisk" | localize }}</div>
  </div>
</ng-container>

<ng-template #loadingTemplate>
  <update-metric-skeleton />
</ng-template>

<ui-loading-indicator [uiSpinning]="loading" [uiIndicator]="loadingTemplate">
  <div class="empty-module" *ngIf="!isActionAllowed('update')">
    <div class="empty-module-title">{{ "permit_only_check_in_metrics" | localize }}</div>
  </div>

  <div class="update-metric-form-wrap" *ngIf="isActionAllowed('update')">
    <div class="update-metric-form" data-test-id="update-metric">
      <div class="form-alerts">
        <ui-alert
          *ngIf="validationStates.sessionStartsOnAlert"
          [uiDescription]="('the_session_for_this_kr_starts_on' | localize) + ' ' + (datepickerOptions.minDate | date : 'MMMM dd, yyyy')"
          uiType="warning"
          tabindex="0"></ui-alert>
        <ui-alert
          *ngIf="validationStates.archivedMetricAlert"
          [uiDescription]="'cant_update_archived' | localize"
          uiType="warning"
          data-test-id="cannot-update-archived-metric"
          tabindex="0"></ui-alert>
        <ui-alert
          *ngIf="validationStates.abandonedObjectiveAlert"
          [uiDescription]="'krs_in_abandoned_objective_cant_be_updated' | localize"
          uiType="warning"
          data-test-id="update-key-result-modal-abandoned-okr-warning"
          tabindex="0"></ui-alert>
        <ui-alert
          *ngIf="validationStates.krPastHardDeadlineAlert"
          [uiDescription]="('kr_past_hard_deadline_can_be_updated_only_by_admins' | localize) + ' ' + (metric.dueDate | date : 'MMMM dd, yyyy HH:mm' : 'UTC') + ' UTC.'"
          uiType="warning"
          data-test-id="cannot-update-archived-metric"
          tabindex="0"></ui-alert>
        <ui-alert
          *ngIf="validationStates.noSessionAlert"
          [uiMessage]="'this_okr_doesnt_belong_to_a_session' | localize"
          [uiDescription]="'updating_wont_affect_objective_progress' | localize"
          uiType="warning"
          tabindex="0"></ui-alert>
        <ui-alert *ngIf="validationStates.hardDeadlineOutsideSession" [uiDescription]="'hard_deadline_outside_session' | localize" uiType="error" tabindex="0"></ui-alert>
        <ui-alert
          *ngIf="validationStates.deadlineAlert"
          [uiDescription]="('hard_deadline_for_kr' | localize) + ' ' + (metric.dueDate | date : 'MMMM dd, yyyy HH:mm' : 'UTC') + ' UTC.'"
          uiType="warning"
          tabindex="0"></ui-alert>
      </div>

      <form [formGroup]="updateMetricForm">
        <div class="form-row" *ngIf="metric.manualType !== 'boolean'">
          <p class="section-name center">{{ "current_value_cap" | localize }}</p>

          <div class="section-field current-value-field">
            <span [attr.aria-label]="statusAriaLabel" [attr.aria-describedby]="statusInfoAriaLabel ? 'kr-update-status-info' : null" tabindex="0">
              <span class="current-value" *ngIf="!metric.cascadeType">{{ metric | metric : "actual" : { additionalInfo: "formatted" } }},&nbsp;</span>
              <span>{{ metric | metric : "targets" : { additionalInfo: "update-metric" } }}</span>
            </span>
            <icon-with-popover
              class="info-popover"
              id="kr-update-status-info"
              *ngIf="statusInfoAriaLabel"
              [content]="statusInfoAriaLabel"
              [a11yLabel]="statusInfoAriaLabel"
              [a11yTabIndex]="-1"
              ghIconName="info"
              trigger="hover"
              placement="right">
            </icon-with-popover>
          </div>
        </div>

        <div class="form-row achieved-or-not-row" *ngIf="validationStates?.showRadioOptions">
          <p class="section-name">{{ "status_cap" | localize }}</p>
          <div class="section-field">
            <ui-radio-group formControlName="newValue">
              <label [uiValue]="metric.initialValue" ui-radio>{{ "not_achieved_yet" | localize }}</label>
              <label [uiValue]="metric.target" ui-radio>{{ "achieved" | localize }}</label>
            </ui-radio-group>
          </div>
        </div>

        <div class="form-row new-value-row" *ngIf="validationStates.showNumericValueField">
          <p class="section-name center">{{ "new_value" | localize }}</p>

          <div class="section-field">
            <metric-number-input-field
              [attr.aria-label]="metric.format.prefix + ' ' + metric.actual + ' ' + metric.format.suffix"
              [a11yLabel]="'new_value' | localize"
              [prefix]="metric.format.prefix"
              [suffix]="metric.format.suffix"
              [defaultValue]="metric.actual"
              formControlName="newValue"
              e2eTestId="actual-value-input-field"></metric-number-input-field>
          </div>
        </div>

        <div class="form-row" *ngIf="validationStates.isDataValid">
          <p class="section-name center">
            {{ "confidence_level_cap" | localize }}
            <span *ngIf="confidenceType === 'numeric'"
              >&nbsp;
              <span *ngIf="confidenceScale === 'minor'">{{ updateMetricForm.controls.confidence.value }}</span>
              <span *ngIf="confidenceScale === 'major'">{{ updateMetricForm.controls.confidence.value * 10 }}/10</span>
            </span>
          </p>

          <div class="section-field">
            <confidence-level-selector formControlName="confidence"></confidence-level-selector>
          </div>
        </div>

        <div class="form-row" *ngIf="validationStates.isDataValid">
          <p class="section-name" id="update-kr-note-rte-label">{{ "note" | localize }} <span *ngIf="metric.sourceMetricId" [attr.aria-hidden]="true">*</span></p>

          <div class="section-field rich-text-editor">
            <rich-text-editor-with-mentions
              [placeholder]="'update_kr_comment_placeholder' | localize"
              [removeAllMentionsAriaLabel]="removeAllMentionsAriaLabel"
              formControlName="note"
              data-test-id="update-comment-input-field"
              a11yLabelledby="update-kr-note-rte-label"></rich-text-editor-with-mentions>
          </div>
        </div>

        <div class="form-row custom-fields" *ngIf="validationStates.isDataValid">
          <editable-custom-fields
            [customFieldsFilter]="filterBySessionIdBound"
            [borderless]="false"
            formControlName="customFields"
            formName="a14-update-metric-form"
            targetType="metric_snapshot"></editable-custom-fields>
        </div>

        <div class="form-row update-date" *ngIf="validationStates.isDataValid">
          <p class="section-name" id="update-date-name">{{ "date_of_update" | localize }}</p>
          <div class="section-field">
            <ui-date-picker
              [uiOpen]="false"
              [uiBackdrop]="true"
              [uiDisabledDate]="shouldDisableUpdateDateBound"
              [attr.aria-label]="updateMetricForm.value.dateOfUpdate | date"
              formControlName="dateOfUpdate"
              a11yLabelledby="update-date-name"
              uiAriaDescribedBy="update-date-description"
              data-test-id="update-date-button"></ui-date-picker>
            <p class="form-item-description" id="update-date-description">{{ "specify_date_past_update" | localize }}</p>
          </div>
        </div>
      </form>

      <div class="buttons">
        <gh-indicator *ngIf="indicators.saving" [ghValue]="indicators.saving" [ghMessage]="'saving' | localize"></gh-indicator>
      </div>
    </div>
  </div>
</ui-loading-indicator>
